/**
 * =============================================================================
 * TeamMemberProfileSettings.js
 * =============================================================================
 * 
 * PURPOSE:
 * Settings panel for team members (non-admin users) to manage their profile.
 * Accessed from the avatar dropdown in the Team Comms header.
 * 
 * FEATURES:
 * - Change display name
 * - Change email (with verification via Supabase)
 * - Set birthday
 * - Upload profile picture (synced to Supabase Storage, MongoDB, and Firestore)
 * - Log out
 * 
 * RELATED FILES:
 * - TeamCommsApp.js - Parent component with header dropdown
 * - ProfilePage.js - Admin version (similar UI)
 * - /backend/routes/user.py - Backend API for profile management
 * - /hooks/useAvatarUpload.js - Unified avatar upload hook
 * 
 * CREATED: January 2026
 * =============================================================================
 */

import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { 
  ArrowLeft, Camera, User, Mail, Calendar, Save, Loader2, 
  LogOut, AlertCircle, CheckCircle2, X, Bell, BellOff, Upload
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import { UserAvatar } from '@/components/shared';
import { useAvatarUpload } from '@/hooks/useAvatarUpload';
import UploadProgress from '@/components/shared/UploadProgress';
import { requestNotificationPermission, isPushSupported } from '../../lib/firebase';
import { IMAGE_ACCEPT, isHeicFile } from '@/lib/imageFiles';

import { DesignToggleRow } from '@/components/shared/DesignToggle';
import { useDesign } from '@/contexts/DesignContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Team Member Profile Settings Component
 * 
 * @param {Object} user - Current user object from Supabase auth
 * @param {Function} onBack - Callback to return to Team Comms
 * @param {Function} onLogout - Callback to handle logout
 * @param {Function} onUpdateUser - Callback to update user state in parent
 */
function TeamMemberProfileSettings({ user, onBack, onLogout, onUpdateUser }) {
  const { isStudio } = useDesign();
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    name: user?.user_metadata?.name || user?.name || '',
    email: user?.email || '',
    birthday: '',
    avatar_url: user?.avatar || ''
  });
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [emailChangeRequested, setEmailChangeRequested] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [showEmailChange, setShowEmailChange] = useState(false);
  const fileInputRef = useRef(null);
  
  // Push notification state
  const [pushSupported] = useState(isPushSupported());
  const [browserPermission, setBrowserPermission] = useState('default');
  const [registeringPush, setRegisteringPush] = useState(false);
  const [hasPushToken, setHasPushToken] = useState(false);
  
  // Use the unified avatar upload hook
  const { uploadAvatar, uploading: uploadingAvatar, progress: uploadProgress, error: uploadError } = useAvatarUpload();

  // Check browser notification permission on mount
  useEffect(() => {
    if (pushSupported && user?.id) {
      setBrowserPermission(Notification.permission);
      // Check if we have a token registered
      const checkStatus = async () => {
        try {
          const response = await fetch(`${API_URL}/api/push/status/${user.id}`);
          if (response.ok) {
            const data = await response.json();
            setHasPushToken(data.active_subscriptions > 0);
          }
        } catch (err) {
          console.error('Error checking push status:', err);
        }
      };
      checkStatus();
    }
  }, [pushSupported, user?.id]);
  
  // Enable/re-register push notifications
  const handleEnablePush = async () => {
    setRegisteringPush(true);
    try {
      console.log('[Settings] Requesting push notification permission...');
      const token = await requestNotificationPermission(user.id);
      if (token) {
        setBrowserPermission('granted');
        setHasPushToken(true);
        toast.success('Push notifications enabled!');
      } else {
        toast.error('Could not enable notifications. Please check your browser settings.');
      }
    } catch (err) {
      console.error('Error enabling push:', err);
      toast.error('Failed to enable notifications');
    } finally {
      setRegisteringPush(false);
    }
  };

  // Fetch extended profile data from MongoDB
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await fetch(`${API_URL}/api/user/profile/${user.id}`);
        if (response.ok) {
          const data = await response.json();
          // Normalize birthday to MM-DD format (handle legacy YYYY-MM-DD)
          let birthday = data.birthday || '';
          if (birthday && birthday.length > 5 && birthday.includes('-')) {
            const parts = birthday.split('-');
            if (parts.length === 3) birthday = `${parts[1]}-${parts[2].substring(0, 2)}`;
          }
          setProfile(prev => ({
            ...prev,
            birthday,
            avatar_url: data.avatar_url || prev.avatar_url
          }));
          if (data.avatar_url) {
            setAvatarPreview(data.avatar_url);
          }
        }
      } catch (err) {
        console.error('Error fetching profile:', err);
      }
    };

    if (user?.id) {
      fetchProfile();
    }
  }, [user?.id]);

  // Handle avatar click to open file picker
  const handleAvatarClick = () => {
    if (!uploadingAvatar) {
      fileInputRef.current?.click();
    }
  };

  // Handle file selection and upload using unified hook
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Show preview immediately — except for HEIC: most browsers cannot draw
    // it and UserAvatar caches whatever URL it is handed, so the current
    // avatar stays until the server's JPEG comes back.
    if (!isHeicFile(file)) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }

    // Upload using the unified hook (syncs to all databases)
    const result = await uploadAvatar(user.id, file, profile.name);
    
    if (result?.success && result.avatarUrl) {
      setProfile(prev => ({ ...prev, avatar_url: result.avatarUrl }));
      setAvatarPreview(result.avatarUrl);
      
      // Update parent component's user state
      if (onUpdateUser) {
        onUpdateUser({ 
          ...user, 
          avatar: result.avatarUrl,
          user_metadata: { ...user.user_metadata, avatar_url: result.avatarUrl } 
        });
      }
      toast.success('Profile photo updated!');
    } else {
      // Upload failed - revert preview and show error
      setAvatarPreview(profile.avatar_url);
      toast.error(result?.error || 'Upload failed. Please try again.');
    }
  };

  // Handle email change request
  const handleEmailChange = async () => {
    if (!newEmail || newEmail === profile.email) {
      toast.error('Please enter a new email address');
      return;
    }

    setSaving(true);
    try {
      // Use Supabase to update email (will send verification to both old and new)
      const { error } = await supabase.auth.updateUser({
        email: newEmail
      });

      if (error) {
        throw error;
      }

      setEmailChangeRequested(true);
      toast.success('Verification email sent! Check both your old and new email.');
    } catch (err) {
      console.error('Error changing email:', err);
      toast.error("Couldn't update your email. Please try again or contact support.");
    } finally {
      setSaving(false);
    }
  };

  // Handle save profile
  const handleSave = async () => {
    setSaving(true);
    try {
      console.log('[TeamMemberSettings] Starting profile save for:', profile.name);
      
      // Update Supabase user metadata
      const { error: authError } = await supabase.auth.updateUser({
        data: { 
          name: profile.name,
          avatar_url: profile.avatar_url
        }
      });

      if (authError) throw authError;

      // Save to backend - this updates MongoDB
      const response = await fetch(`${API_URL}/api/user/profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          name: profile.name,
          email: profile.email,
          birthday: profile.birthday,
          avatar_url: profile.avatar_url
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save profile');
      }

      // CRITICAL: Sync to Firestore (for Messages/DMs display)
      try {
        console.log('[TeamMemberSettings] Syncing to Firestore...');
        const { updateUserProfile } = await import('@/lib/firestore');
        await updateUserProfile(user.id, { 
          name: profile.name, 
          avatar: profile.avatar_url 
        });
        console.log('[TeamMemberSettings] Firestore profile updated successfully');
      } catch (firestoreErr) {
        console.error('[TeamMemberSettings] Firestore sync error:', firestoreErr);
        toast.info('Saved! Your display name in messages may take a moment to update.');
      }

      // Update parent component's user state
      if (onUpdateUser) {
        onUpdateUser({ 
          ...user, 
          name: profile.name, 
          avatar: profile.avatar_url,
          user_metadata: { 
            ...user.user_metadata, 
            name: profile.name, 
            avatar_url: profile.avatar_url 
          } 
        });
      }
      toast.success('Profile saved successfully!');
    } catch (err) {
      console.error('Error saving profile:', err);
      toast.error('Couldn\'t save your profile. Please check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  /* ---------------------------------------------------------------------
     Studio (docs/app-map/studio/mocks/settings-a.md, frames 7-8, 11-12).
     The member page in the settings frame: the profile card with its save
     bar, the push card, and a last card carrying Sign out. Same state, same
     handlers, same gating as classic.
     ------------------------------------------------------------------- */
  if (isStudio) {
    const studioPush = () => {
      if (!pushSupported) {
        return (
          <div className="st-notice st-notice-warn">
            <BellOff /><span>Push notifications are not supported in this browser</span>
          </div>
        );
      }
      if (browserPermission === 'denied') {
        return (
          <div className="st-notice st-notice-bad">
            <BellOff />
            <span>
              <b>Notifications blocked</b><br />
              Please enable in your device&apos;s Settings → Safari → Notifications
            </span>
          </div>
        );
      }
      if (browserPermission === 'granted' && hasPushToken) {
        return (
          <div className="st-noticestack">
            <div className="st-notice st-notice-ok" style={{ width: '100%' }}>
              <CheckCircle2 /><span>Push notifications enabled</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleEnablePush}
              disabled={registeringPush}
              className="st-sbtn st-sbtn-sec st-sbtn-sm"
              data-testid="reregister-push-btn"
            >
              {registeringPush ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bell className="w-3 h-3" />}
              Re-register push token
            </Button>
          </div>
        );
      }
      return (
        <Button
          variant="outline"
          onClick={handleEnablePush}
          disabled={registeringPush}
          className="st-sbtn st-sbtn-sec"
          data-testid="enable-push-btn"
        >
          {registeringPush ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
          Enable push notifications
        </Button>
      );
    };

    return (
      <div className="st-page">
        <header className="st-sethdr">
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="st-ibtn"
            aria-label="Back"
            title="Back"
            data-testid="settings-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <span className="st-setglyph"><User /></span>
          <h1 className="st-settitle">Profile settings</h1>
        </header>

        <main className="st-setwrap">
          <div className="st-setcard">
            <div className="st-seth">
              <h2 className="st-setct">Your profile</h2>
              <p className="st-setcd">Manage your personal information</p>
            </div>
            <div className="st-setb">
              {/* Photo band */}
              <div className="st-srow st-photo">
                <div className="st-srl" style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <span
                    className="st-avwrap"
                    data-uploading={uploadingAvatar ? 'true' : 'false'}
                    onClick={handleAvatarClick}
                    data-testid="avatar-upload-btn"
                    style={{ cursor: uploadingAvatar ? 'default' : 'pointer' }}
                  >
                    <span className="st-avbox">
                      <UserAvatar
                        userId={user?.id}
                        name={profile.name || profile.email}
                        avatarUrl={avatarPreview || profile.avatar_url}
                        size="2xl"
                        className="w-full h-full rounded-none"
                      />
                    </span>
                    {uploadingAvatar && (
                      <span className="st-avring">
                        <UploadProgress progress={uploadProgress} size={36} strokeWidth={2} />
                      </span>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={IMAGE_ACCEPT}
                      onChange={handleFileChange}
                      className="hidden"
                      disabled={uploadingAvatar}
                    />
                  </span>
                  <div>
                    <span className="st-srlbl">Profile photo</span>
                    <span className="st-srhelp">
                      {uploadingAvatar ? 'Uploading…' : 'JPG, PNG or HEIC · up to 5 MB'}
                    </span>
                  </div>
                </div>
                <div className="st-srctl">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAvatarClick}
                    disabled={uploadingAvatar}
                    className="st-sbtn st-sbtn-sec st-sbtn-sm"
                  >
                    <Upload className="w-4 h-4" />
                    Upload photo
                  </Button>
                </div>
              </div>

              {/* Display name */}
              <div className="st-srow st-wrapctl">
                <div className="st-srl">
                  <label className="st-srlbl" htmlFor="name">Display name</label>
                </div>
                <div className="st-srctl">
                  <Input
                    id="name"
                    value={profile.name}
                    onChange={(e) => setProfile(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Your name"
                    className="st-setin"
                    style={{ width: '240px' }}
                    data-testid="profile-name-input"
                  />
                </div>
              </div>

              {/* Email */}
              <div className="st-srow st-wrapctl">
                <div className="st-srl">
                  <label className="st-srlbl" htmlFor="email">Email</label>
                </div>
                <div className="st-srctl">
                  <Input
                    id="email"
                    type="email"
                    value={profile.email}
                    disabled
                    className="st-setin st-ro"
                    style={{ width: '240px' }}
                  />
                  {!showEmailChange && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowEmailChange(true)}
                      className="st-sbtn st-sbtn-ghost st-sbtn-sm"
                    >
                      Change email address
                    </Button>
                  )}
                </div>
              </div>

              {/* Change email, inline: the member flow verifies */}
              {showEmailChange && (emailChangeRequested ? (
                <div className="st-notice st-notice-ok" style={{ marginBottom: '12px' }}>
                  <CheckCircle2 />
                  <span>
                    <b>Verification email sent!</b><br />
                    Check both your old and new email to confirm the change.
                  </span>
                </div>
              ) : (
                <div className="st-inline">
                  <span className="st-inline-t">New email address</span>
                  <div className="st-inline-f">
                    <label htmlFor="st-new-email">New email</label>
                    <Input
                      id="st-new-email"
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="name@church.org"
                      className="st-setin"
                      data-testid="new-email-input"
                    />
                  </div>
                  <div className="st-inline-h">
                    A verification link will be sent to both your current and new email addresses.
                  </div>
                  <div className="st-inline-a">
                    <Button
                      variant="ghost"
                      onClick={() => { setShowEmailChange(false); setNewEmail(''); }}
                      className="st-sbtn st-sbtn-ghost"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleEmailChange}
                      disabled={saving || !newEmail}
                      className="st-sbtn st-sbtn-sec"
                      data-testid="confirm-email-change-btn"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      Send verification email
                    </Button>
                  </div>
                </div>
              ))}

              {/* Birthday */}
              <div className="st-srow st-wrapctl">
                <div className="st-srl">
                  <span className="st-srlbl">Birthday</span>
                  <span className="st-srhelp">Used for birthday celebrations in Team Comms</span>
                </div>
                <div className="st-srctl">
                  <select
                    data-testid="profile-birthday-month"
                    value={profile.birthday ? profile.birthday.split('-')[0] : ''}
                    onChange={(e) => {
                      const month = e.target.value;
                      const day = profile.birthday ? profile.birthday.split('-')[1] : '';
                      setProfile(prev => ({ ...prev, birthday: month && day ? `${month}-${day}` : month ? `${month}-` : '' }));
                    }}
                    className="st-setsel"
                    style={{ width: '150px' }}
                  >
                    <option value="">Month</option>
                    {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                      <option key={i} value={String(i + 1).padStart(2, '0')}>{m}</option>
                    ))}
                  </select>
                  <select
                    data-testid="profile-birthday-day"
                    value={profile.birthday ? profile.birthday.split('-')[1] : ''}
                    onChange={(e) => {
                      const month = profile.birthday ? profile.birthday.split('-')[0] : '';
                      const day = e.target.value;
                      setProfile(prev => ({ ...prev, birthday: month && day ? `${month}-${day}` : '' }));
                    }}
                    className="st-setsel"
                    style={{ width: '88px' }}
                  >
                    <option value="">Day</option>
                    {Array.from({length: 31}, (_, i) => (
                      <option key={i} value={String(i + 1).padStart(2, '0')}>{i + 1}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="st-savebar">
              <span />
              <Button
                onClick={handleSave}
                disabled={saving || uploadingAvatar}
                className="st-sbtn st-sbtn-pri"
                data-testid="save-profile-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Save changes
              </Button>
            </div>
          </div>

          <div className="st-setcard">
            <div className="st-seth">
              <h2 className="st-setct">Push notifications</h2>
              <p className="st-setcd">Get notified about new posts, comments, and messages</p>
            </div>
            <div className="st-setb">{studioPush()}</div>
          </div>

          <div className="st-setcard">
            <div className="st-setb" style={{ paddingTop: '16px' }}>
              <DesignToggleRow user={user} userId={user?.id} className="st-srow" />
              <div className="st-srow">
                <div className="st-srl">
                  <span className="st-srlbl">Signed in as {profile.email}</span>
                </div>
                <div className="st-srctl">
                  <Button
                    variant="ghost"
                    onClick={onLogout}
                    className="st-sbtn st-sbtn-danger"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign out
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 dark:from-[#1e1f22] to-slate-100 dark:to-[#2d2f33]">
      {/* Header */}
      <header className="bg-white/80 dark:bg-[#2d2f33]/80 backdrop-blur-sm border-b border-slate-200 dark:border-white/10 sticky top-0 z-10">
        <div className="max-w-xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onBack} 
              className="h-9 w-9 -ml-2"
              data-testid="settings-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center">
                <User className="w-4 h-4 text-white" />
              </div>
              <h1 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Profile Settings</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 py-6">
        <Card className="shadow-sm border border-slate-200 dark:border-white/10 bg-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-xl">Your Profile</CardTitle>
            <CardDescription className="text-slate-500 dark:text-slate-400">
              Manage your personal information
            </CardDescription>
          </CardHeader>
          
          <CardContent className="pt-6 space-y-6">
            {/* Avatar Section - Using unified UserAvatar component */}
            <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-[#1e1f22] rounded-xl border border-slate-100 dark:border-white/10">
              <div 
                className="relative cursor-pointer group"
                onClick={handleAvatarClick}
                data-testid="avatar-upload-btn"
              >
                <div className="w-20 h-20 rounded-xl overflow-hidden border-2 border-white shadow-lg">
                  {uploadingAvatar ? (
                    <div className="w-full h-full bg-slate-200 dark:bg-[#3e4145] flex items-center justify-center">
                      <UploadProgress progress={uploadProgress} size={32} className="text-blue-500" />
                    </div>
                  ) : (
                    <UserAvatar 
                      userId={user?.id}
                      name={profile.name || profile.email}
                      avatarUrl={avatarPreview || profile.avatar_url}
                      size="2xl"
                      className="w-full h-full rounded-none"
                    />
                  )}
                </div>
                
                {/* Upload overlay */}
                {!uploadingAvatar && (
                  <div className="absolute inset-0 rounded-xl bg-black/50 opacity-0 group-hover:opacity-100 transition-all duration-200 flex items-center justify-center">
                    <Camera className="w-6 h-6 text-white" />
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={IMAGE_ACCEPT}
                  onChange={handleFileChange}
                  className="hidden"
                  disabled={uploadingAvatar}
                />
              </div>
              <div>
                <h3 className="font-medium text-slate-800 dark:text-slate-100">Profile Photo</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                  {uploadingAvatar ? 'Uploading...' : 'Tap to change'}
                </p>
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">JPG, PNG or HEIC • Max 5MB</p>
              </div>
            </div>

            {/* Form Fields */}
            <div className="space-y-5">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200">
                  <User className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                  Display Name
                </Label>
                <Input
                  id="name"
                  value={profile.name}
                  onChange={(e) => setProfile(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Your name"
                  className="h-11"
                  data-testid="profile-name-input"
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200">
                  <Mail className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                  Email Address
                </Label>
                <div className="space-y-2">
                  <Input
                    id="email"
                    type="email"
                    value={profile.email}
                    disabled
                    className="h-11 bg-slate-50 dark:bg-[#1e1f22] text-slate-500 dark:text-slate-400"
                  />
                  {!showEmailChange ? (
                    <Button 
                      variant="link" 
                      size="sm" 
                      onClick={() => setShowEmailChange(true)}
                      className="h-auto p-0 text-blue-600 dark:text-blue-400 text-sm"
                    >
                      Change email address
                    </Button>
                  ) : emailChangeRequested ? (
                    <div className="flex items-start gap-2 p-3 bg-green-50 dark:bg-green-950/40 rounded-lg border border-green-200 dark:border-green-900">
                      <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-green-800 dark:text-green-200">Verification email sent!</p>
                        <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">
                          Check both your old and new email to confirm the change.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2 p-3 bg-slate-50 dark:bg-[#1e1f22] rounded-lg border border-slate-200 dark:border-white/10">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-200">New email address</span>
                        <button 
                          onClick={() => { setShowEmailChange(false); setNewEmail(''); }}
                          className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-200"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <Input
                        type="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        placeholder="Enter new email"
                        className="h-10"
                        data-testid="new-email-input"
                      />
                      <div className="flex items-start gap-2 text-xs text-slate-500 dark:text-slate-400">
                        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>A verification link will be sent to both your current and new email addresses.</span>
                      </div>
                      <Button 
                        size="sm" 
                        onClick={handleEmailChange}
                        disabled={saving || !newEmail}
                        className="w-full"
                        data-testid="confirm-email-change-btn"
                      >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Send Verification Email
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Birthday */}
              <div className="space-y-2">
                <Label htmlFor="birthday" className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200">
                  <Calendar className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                  Birthday
                </Label>
                <div className="flex gap-3">
                  <select
                    data-testid="profile-birthday-month"
                    value={profile.birthday ? profile.birthday.split('-')[0] : ''}
                    onChange={(e) => {
                      const month = e.target.value;
                      const day = profile.birthday ? profile.birthday.split('-')[1] : '';
                      setProfile(prev => ({ ...prev, birthday: month && day ? `${month}-${day}` : month ? `${month}-` : '' }));
                    }}
                    className="flex-1 h-11 px-3 rounded-md border border-slate-200 dark:border-white/10 bg-card text-sm"
                  >
                    <option value="">Month</option>
                    {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                      <option key={i} value={String(i + 1).padStart(2, '0')}>{m}</option>
                    ))}
                  </select>
                  <select
                    data-testid="profile-birthday-day"
                    value={profile.birthday ? profile.birthday.split('-')[1] : ''}
                    onChange={(e) => {
                      const month = profile.birthday ? profile.birthday.split('-')[0] : '';
                      const day = e.target.value;
                      setProfile(prev => ({ ...prev, birthday: month && day ? `${month}-${day}` : '' }));
                    }}
                    className="w-24 h-11 px-3 rounded-md border border-slate-200 dark:border-white/10 bg-card text-sm"
                  >
                    <option value="">Day</option>
                    {Array.from({length: 31}, (_, i) => (
                      <option key={i} value={String(i + 1).padStart(2, '0')}>{i + 1}</option>
                    ))}
                  </select>
                </div>
                <p className="text-xs text-slate-400 dark:text-slate-500">
                  Used for birthday celebrations in Team Comms
                </p>
              </div>
            </div>

            {/* Save Button */}
            <div className="pt-4 border-t border-slate-100 dark:border-white/10">
              <Button 
                onClick={handleSave} 
                disabled={saving || uploadingAvatar} 
                className="w-full h-11 gap-2"
                data-testid="save-profile-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save Changes
              </Button>
            </div>

            {/* Push Notifications Section */}
            <div className="pt-4 border-t border-slate-100 dark:border-white/10">
              <div className="space-y-3">
                <Label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200">
                  <Bell className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                  Push Notifications
                </Label>
                
                {!pushSupported ? (
                  <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-950/40 rounded-lg border border-amber-200 dark:border-amber-900">
                    <BellOff className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                    <span className="text-sm text-amber-700 dark:text-amber-300">
                      Push notifications are not supported in this browser
                    </span>
                  </div>
                ) : browserPermission === 'denied' ? (
                  <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950/40 rounded-lg border border-red-200 dark:border-red-900">
                    <BellOff className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-red-700 dark:text-red-300">Notifications blocked</p>
                      <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">
                        Please enable in your device&apos;s Settings → Safari → Notifications
                      </p>
                    </div>
                  </div>
                ) : browserPermission === 'granted' && hasPushToken ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-950/40 rounded-lg border border-green-200 dark:border-green-900">
                      <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                      <span className="text-sm text-green-700 dark:text-green-300">Push notifications enabled</span>
                    </div>
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={handleEnablePush}
                      disabled={registeringPush}
                      className="w-full text-xs"
                      data-testid="reregister-push-btn"
                    >
                      {registeringPush ? (
                        <Loader2 className="w-3 h-3 animate-spin mr-2" />
                      ) : (
                        <Bell className="w-3 h-3 mr-2" />
                      )}
                      Re-register Push Token
                    </Button>
                  </div>
                ) : (
                  <Button 
                    onClick={handleEnablePush}
                    disabled={registeringPush}
                    className="w-full h-11 gap-2 bg-indigo-600 hover:bg-indigo-700"
                    data-testid="enable-push-btn"
                  >
                    {registeringPush ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Bell className="w-4 h-4" />
                    )}
                    Enable Push Notifications
                  </Button>
                )}
                
                <p className="text-xs text-slate-400 dark:text-slate-500">
                  Get notified about new posts, comments, and messages
                </p>
              </div>
            </div>

            {/* Appearance — design language opt-in (docs/app-map/102 §1) */}
            <div className="pt-4 border-t border-slate-100 dark:border-white/10">
              <DesignToggleRow user={user} userId={user?.id} />
            </div>

            {/* Logout Section */}
            <div className="pt-4 border-t border-slate-100 dark:border-white/10">
              <Button 
                variant="outline" 
                onClick={onLogout} 
                className="w-full h-11 gap-2 text-red-600 dark:text-red-400 border-red-200 dark:border-red-900 hover:bg-red-50 dark:hover:bg-red-500/15 hover:border-red-300"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
                Log Out
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

export default TeamMemberProfileSettings;
